import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { siteNames, createUrlResolver, HOMEPAGE_LANGUAGE_OVERRIDE_PATH } from "@seek/melways-sites";
import { useTranslations } from "@vocab/react";
import { Link, Hidden, Box, Text, Stack, Divider, TextLink, ContentBlock } from "braid-design-system";
import * as React from "react";
import { useState } from "react";
import { translations as translations$3 } from "../../EmployerFooter/.vocab/index.mjs";
import { analyticsEvents, analyticsGlobalFooterEvents, filterLinksOnSiteAndHref, AuthenticatedFooterLinks } from "./AuthenticatedFooterLinks/AuthenticatedFooterLinks.mjs";
import omit from "lodash/omit.js";
import { BasicLink } from "./GeneralLinks/BasicLink.mjs";
import { ContactUsLink } from "./GeneralLinks/ContactUsLink.mjs";
import { NeedHelpLink } from "./GeneralLinks/NeedHelpLink.mjs";
import { PartnersLink } from "./GeneralLinks/PartnersLink.mjs";
import * as pageUrl from "@seek/adv-constants";
import { INTERNATIONAL_PARTNER_URL_BD_JOBS, INTERNATIONAL_PARTNER_URL_JOBS_DB, INTERNATIONAL_PARTNER_URL_JOB_STREET, INTERNATIONAL_PARTNER_URL_JORA_AU, INTERNATIONAL_PARTNER_URL_JORA, URL_SEEK_CANDIDATE_AU, URL_SEEK_CANDIDATE_NZ, INTERNATIONAL_PARTNER_URL_ZHAOPIN, PARTNER_SERVICES_URL_CERTSY, PARTNER_SERVICES_URL_GRAD_CONNECTION, PARTNER_SERVICES_URL_GO1, INTERNATIONAL_PARTNER_URL_JOBADDER } from "@seek/adv-constants";
import { EMPLOYER_HELP_CENTRE_URL } from "@seek/shared-constants";
import { translations } from "../../EmployerFooter/GeneralLinks/.vocab/index.mjs";
import { translations as translations$1 } from "../../EmployerFooter/InternationalPartnerLinks/.vocab/index.mjs";
import { translations as translations$2 } from "../../EmployerFooter/PartnerServiceLinks/.vocab/index.mjs";
import { authLinksContainer, generalLinksContainer } from "./EmployerFooter.css.mjs";
const DefaultLinkRenderer = (props) => {
  const validClassName = props.className === void 0 || typeof props.className === "string";
  if (!validClassName) {
    throw new Error(
      `Expected className to be string or undefined, ${typeof props.className} provided instead`
    );
  }
  return /* @__PURE__ */ jsx(Link, { ...props });
};
const getAuthenticationStatus = (identity) => {
  if (Boolean(identity) && Boolean(identity == null ? void 0 : identity.email)) {
    return "authenticated";
  }
  if (identity === null) {
    return "unauthenticated";
  }
  return "pending";
};
const isAuthenticated = (authenticationStatus) => authenticationStatus === "authenticated";
const isUnauthenticated = (authenticationStatus) => authenticationStatus === "unauthenticated";
const isPending = (authenticationStatus) => authenticationStatus === "pending";
const defaultPermissionOptions = {
  canCreateJobs: false,
  canManageBrand: false,
  canManageCompanyProfile: false,
  canManageUsers: false,
  canPayInvoices: false,
  canViewInvoiceHistory: false,
  canViewManagerReports: false
};
const defaultLocale = "en-AU";
const defaultSite = siteNames.employer.seek.au;
const defaultValue = {
  localisedContactInfo: {},
  authenticationStatus: "pending",
  shouldDisplayActions: true,
  shouldDisplayCtaButton: true,
  hasError: false,
  basic: false,
  isSignedInToAccount: false,
  isAgency: false,
  hasAlert: false,
  advertiserActivationPending: false,
  permissionOptions: defaultPermissionOptions,
  impersonationContext: null,
  site: defaultSite,
  locale: defaultLocale,
  urlResolver: createUrlResolver({
    language: defaultLocale,
    site: defaultSite,
    staging: false
  }),
  handleInteractionTracking: () => {
  }
};
const EmployerHeaderContext = React.createContext(
  defaultValue
);
const useEmployerHeaderContext = () => {
  const context = React.useContext(EmployerHeaderContext);
  if (!context) {
    throw new Error(
      "EmployerHeaderContext must be used within a EmployerHeaderProvider"
    );
  }
  return context;
};
const EmployerHeaderProvider = ({
  children,
  identityContext: identity,
  user,
  advertiser,
  basic,
  error,
  site,
  locale,
  localisedContactInfo,
  urlResolver,
  handleInteractionTracking
}) => {
  const authenticationStatus = getAuthenticationStatus(identity);
  const identityIsUnauthenticated = isUnauthenticated(authenticationStatus);
  const isSignedInToAccount = Boolean(advertiser);
  const isAgency = Boolean(advertiser == null ? void 0 : advertiser.isAgency);
  const permissionOptions = (advertiser == null ? void 0 : advertiser.permissionOptions) ?? defaultValue.permissionOptions;
  const showOnCreditHoldMessage = Boolean(advertiser == null ? void 0 : advertiser.showOnCreditHoldMessage);
  const shouldDisplayActions = identityIsUnauthenticated && !isAgency;
  const shouldDisplayCtaButton = (identityIsUnauthenticated || permissionOptions.canCreateJobs) && !isAgency;
  const hasError = typeof error !== "undefined";
  const name = user == null ? void 0 : user.name;
  const advertiserName = advertiser == null ? void 0 : advertiser.name;
  const dropDownLinkText = advertiserName || (user == null ? void 0 : user.firstName) || (identity == null ? void 0 : identity.email.split("@")[0]);
  const advertiserActivationPending = (advertiser == null ? void 0 : advertiser.isActivationPending) || false;
  const billingId = advertiser == null ? void 0 : advertiser.billingId;
  let resolvedSwitchAccountsHref;
  if (typeof (advertiser == null ? void 0 : advertiser.switchAccountsHref) === "string") {
    resolvedSwitchAccountsHref = urlResolver({
      path: advertiser == null ? void 0 : advertiser.switchAccountsHref
    });
  }
  const switchAccountsHref = resolvedSwitchAccountsHref;
  let impersonationContext = null;
  if (identity == null ? void 0 : identity.act) {
    let additionalInfo;
    if (advertiserName) {
      additionalInfo = `${advertiserName} ${billingId}`;
    }
    impersonationContext = {
      ...identity.act,
      subjectEmail: identity.email,
      additionalInfo
    };
  }
  const hasAlert = impersonationContext !== null || showOnCreditHoldMessage;
  return /* @__PURE__ */ jsx(
    EmployerHeaderContext.Provider,
    {
      value: {
        urlResolver,
        site,
        locale,
        authenticationStatus,
        shouldDisplayActions,
        shouldDisplayCtaButton,
        hasError,
        basic,
        isSignedInToAccount,
        isAgency,
        hasAlert,
        permissionOptions,
        name,
        advertiserName,
        dropDownLinkText,
        advertiserActivationPending,
        billingId,
        switchAccountsHref,
        impersonationContext,
        localisedContactInfo,
        handleInteractionTracking
      },
      children
    }
  );
};
const linkTags = {
  LINK_TAG_CONTACT_US: "link-tag-contact-us",
  LINK_TAG_HELP_CENTRE: "link-tag-help-centre",
  LINK_TAG_INTERNATIONAL_PARTNERS_HASH: "link-tag-international-partners-hash",
  LINK_TAG_PARTNER_SERVICES_HASH: "link-tag-partner-services-hash",
  LINK_TAG_SUPPORT_TERMS: "link-tag-support-terms"
};
const FooterLinkSeparator = () => /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: /* @__PURE__ */ jsx(Box, { paddingX: "small", children: /* @__PURE__ */ jsx(Text, { size: "small", children: "|" }) }) });
function addSeparatorBetween(objects, value, index) {
  if (!Array.isArray(objects)) {
    return [objects, /* @__PURE__ */ jsx(FooterLinkSeparator, {}, `${index}-separator`), value];
  }
  return objects.concat(
    /* @__PURE__ */ jsx(FooterLinkSeparator, {}, `${index}-separator`),
    value
  );
}
const useGeneralLinksItems = (site, urlResolver, localisedContactInfo) => {
  const { t } = useTranslations(translations);
  const contactUsLink = (localisedContactInfo == null ? void 0 : localisedContactInfo.contactUsLink) || urlResolver({
    path: pageUrl.URL_CONTACTUS
  });
  const helpCentreLink = (localisedContactInfo == null ? void 0 : localisedContactInfo.helpCentreLink) || EMPLOYER_HELP_CENTRE_URL[site];
  const generalLinks = [
    {
      id: "aboutSeek",
      label: t("About SEEK"),
      href: urlResolver({
        product: "candidate",
        path: pageUrl.URL_CANDIDATE_ABOUT
      }),
      rel: "noopener noreferrer",
      "data-analytics": analyticsEvents.TRACK_ABOUT,
      analyticsEvent: analyticsGlobalFooterEvents.about
    },
    {
      id: "internationalPartners",
      label: t("International partners"),
      href: urlResolver({
        path: pageUrl.URL_INTERNATIONAL_PARTNERS_HASH
      }),
      "data-link-tag": linkTags.LINK_TAG_INTERNATIONAL_PARTNERS_HASH,
      "data-analytics": analyticsEvents.TRACK_INTERNATIONAL_PARTNERS,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartners
    },
    {
      id: "partnerServices",
      label: t("Partner services"),
      href: urlResolver({
        path: pageUrl.URL_PARTNER_SERVICES_HASH
      }),
      "data-link-tag": linkTags.LINK_TAG_PARTNER_SERVICES_HASH,
      "data-analytics": analyticsEvents.TRACK_PARTNER_SERVICES,
      analyticsEvent: analyticsGlobalFooterEvents.partnerServices
    },
    {
      id: "securityPrivacy",
      label: t("SecurityPrivacy"),
      href: urlResolver({
        product: "candidate",
        path: pageUrl.URL_SECURITY_PRIVACY
      }),
      rel: "noopener noreferrer",
      "data-analytics": analyticsEvents.TRACK_SECURITY_PRIVACY,
      analyticsEvent: analyticsGlobalFooterEvents.securityPrivacy
    },
    {
      id: "termsAndConditions",
      label: t("Terms & conditions"),
      href: urlResolver({
        path: pageUrl.URL_SUPPORT_TERMS
      }),
      "data-link-tag": linkTags.LINK_TAG_SUPPORT_TERMS,
      "data-analytics": analyticsEvents.TRACK_TERMS,
      analyticsEvent: analyticsGlobalFooterEvents.termsAndConditions
    },
    {
      id: "contactUs",
      label: t("Contact us"),
      href: contactUsLink,
      "data-link-tag": linkTags.LINK_TAG_CONTACT_US,
      "data-analytics": analyticsEvents.TRACK_CONTACT_US,
      specificSite: [siteNames.employer.seek.au, siteNames.employer.seek.nz],
      analyticsEvent: analyticsGlobalFooterEvents.contactUs
    },
    {
      id: "helpCentre",
      label: t("Help Centre"),
      href: helpCentreLink,
      "data-link-tag": linkTags.LINK_TAG_HELP_CENTRE,
      "data-analytics": analyticsEvents.TRACK_HELP_CENTRE,
      specificSite: [
        siteNames.employer.seek.hk,
        siteNames.employer.seek.th,
        siteNames.employer.seek.id,
        siteNames.employer.seek.my,
        siteNames.employer.seek.ph,
        siteNames.employer.seek.sg
      ],
      analyticsEvent: analyticsGlobalFooterEvents.helpCentre
    },
    {
      id: "seekJobsSite",
      label: t("SEEK jobs site"),
      href: urlResolver({
        product: "candidate",
        path: HOMEPAGE_LANGUAGE_OVERRIDE_PATH
      }),
      rel: "noopener noreferrer",
      "data-analytics": analyticsEvents.TRACK_JOBS_SITE,
      analyticsEvent: analyticsGlobalFooterEvents.jobsSite
    }
  ];
  return {
    generalLinks: generalLinks.filter(
      (item) => filterLinksOnSiteAndHref(item, site)
    )
  };
};
const GeneralLinks = ({
  internationalPartnersChevronDirection,
  partnerServicesChevronDirection,
  linkRenderer,
  localisedContactInfo,
  site,
  urlResolver,
  onClickInternationalPartners,
  onClickPartnerServices,
  onTrackedItemInteraction = () => {
  }
}) => {
  const { generalLinks } = useGeneralLinksItems(
    site,
    urlResolver,
    localisedContactInfo
  );
  const renderLink = ({ label, href, ...restProps }, index) => {
    const linkTag = restProps["data-link-tag"];
    const key = `footer-general-link-${index}`;
    const analyticsEvent = restProps.analyticsEvent;
    const props = omit(restProps, ["analyticsEvent"]);
    const makeClickHandler = (handler) => (event) => {
      if (analyticsEvent) {
        onTrackedItemInteraction(analyticsEvent);
      }
      if (handler) {
        handler(event);
      }
      if (props.onClick) {
        props.onClick(event);
      }
    };
    if (linkTag === linkTags.LINK_TAG_CONTACT_US) {
      return /* @__PURE__ */ jsx(
        ContactUsLink,
        {
          linkRenderer,
          contactNumber: localisedContactInfo == null ? void 0 : localisedContactInfo.contactNumber,
          href: (localisedContactInfo == null ? void 0 : localisedContactInfo.contactUsLink) || href,
          site,
          label,
          onClick: makeClickHandler(),
          onTrackedItemInteraction,
          ...props
        },
        key
      );
    }
    if (linkTag === linkTags.LINK_TAG_HELP_CENTRE) {
      return /* @__PURE__ */ jsx(
        NeedHelpLink,
        {
          linkRenderer,
          href: (localisedContactInfo == null ? void 0 : localisedContactInfo.helpCentreLink) || href,
          label,
          onClick: makeClickHandler(),
          ...props
        },
        key
      );
    }
    if (href && linkTag === linkTags.LINK_TAG_INTERNATIONAL_PARTNERS_HASH) {
      return /* @__PURE__ */ jsx(
        PartnersLink,
        {
          linkRenderer,
          href,
          partnersChevronDirection: internationalPartnersChevronDirection,
          name: label,
          onClick: makeClickHandler(onClickInternationalPartners),
          ...props
        },
        key
      );
    }
    if (href && linkTag === linkTags.LINK_TAG_PARTNER_SERVICES_HASH) {
      return /* @__PURE__ */ jsx(
        PartnersLink,
        {
          linkRenderer,
          href,
          partnersChevronDirection: partnerServicesChevronDirection,
          name: label,
          onClick: makeClickHandler(onClickPartnerServices),
          ...props
        },
        key
      );
    }
    if (href) {
      return /* @__PURE__ */ jsx(
        BasicLink,
        {
          href,
          name: label,
          linkRenderer,
          onClick: makeClickHandler(),
          ...props
        },
        key
      );
    }
  };
  return /* @__PURE__ */ jsx(Box, { display: ["block", "flex"], flexWrap: "wrap", justifyContent: "center", children: generalLinks.map(renderLink).reduce(
    // @ts-expect-error
    addSeparatorBetween
  ) });
};
const useInternationalPartnerLinksItems = () => {
  const { t } = useTranslations(translations$1);
  const internationalLinksRow1 = [
    {
      labelText: t("Bangladesh"),
      linkText: t("Bdjobs"),
      href: INTERNATIONAL_PARTNER_URL_BD_JOBS,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartnersBdJobs
    },
    {
      labelText: t("SE Asia"),
      linkText: t("jobsDB"),
      href: INTERNATIONAL_PARTNER_URL_JOBS_DB,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartnersJobsdb
    },
    {
      labelText: t("SE Asia"),
      linkText: t("JobStreet"),
      href: INTERNATIONAL_PARTNER_URL_JOB_STREET,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartnersJobstreet
    },
    {
      labelText: t("Australia"),
      linkText: t("Jora"),
      href: INTERNATIONAL_PARTNER_URL_JORA_AU,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartnersJoraAustralia
    },
    {
      labelText: t("Worldwide"),
      linkText: t("Jora"),
      href: INTERNATIONAL_PARTNER_URL_JORA,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartnersJoraWorldwide
    }
  ];
  const internationalLinksRow2 = [
    {
      labelText: t("Australia"),
      linkText: t("SEEK"),
      href: URL_SEEK_CANDIDATE_AU,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartnersSeekAustralia
    },
    {
      labelText: t("New Zealand"),
      linkText: t("SEEK"),
      href: URL_SEEK_CANDIDATE_NZ,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartnersSeekNewZealand
    },
    {
      labelText: t("China"),
      linkText: t("Zhaopin"),
      href: INTERNATIONAL_PARTNER_URL_ZHAOPIN,
      analyticsEvent: analyticsGlobalFooterEvents.internationalPartnersZhaopin
    }
  ];
  return {
    internationalLinksRow1,
    internationalLinksRow2
  };
};
const InternationalPartnerLinks = ({
  onTrackedItemInteraction = () => {
  }
}) => {
  const { internationalLinksRow1, internationalLinksRow2 } = useInternationalPartnerLinksItems();
  const handleClick = (event) => {
    if (event) {
      onTrackedItemInteraction(event);
    }
  };
  const renderLink = ({ href, linkText, labelText, analytics, analyticsEvent }, index) => /* @__PURE__ */ jsxs(Box, { display: "flex", flexWrap: "nowrap", children: [
    /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(
      TextLink,
      {
        href,
        data: { analytics },
        rel: "noopener noreferrer",
        onClick: () => {
          handleClick(analyticsEvent);
        },
        children: linkText
      }
    ) }),
    "   ",
    /* @__PURE__ */ jsx(Text, { size: "small", children: labelText })
  ] }, index);
  return /* @__PURE__ */ jsx(
    Box,
    {
      id: "footer-international",
      "data-automation": "internationalPartnersFooter-text",
      children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsx(Divider, {}),
        /* @__PURE__ */ jsx(
          Box,
          {
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            flexWrap: "wrap",
            children: internationalLinksRow1.map(renderLink).reduce(
              // @ts-expect-error
              addSeparatorBetween
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            flexWrap: "wrap",
            marginBottom: "small",
            children: internationalLinksRow2.map(renderLink).reduce(
              // @ts-expect-error
              addSeparatorBetween
            )
          }
        )
      ] })
    }
  );
};
const usePartnerServiceLinksItems = () => {
  const { t } = useTranslations(translations$2);
  const partnerLinksRow = [
    {
      linkText: t("Certsy"),
      href: PARTNER_SERVICES_URL_CERTSY,
      analytics: analyticsEvents.TRACK_CERTSY,
      analyticsEvent: analyticsGlobalFooterEvents.partnerServicesCertsy
    },
    {
      linkText: t("GradConnection"),
      href: PARTNER_SERVICES_URL_GRAD_CONNECTION,
      analytics: analyticsEvents.TRACK_GRADCONNECTION,
      analyticsEvent: analyticsGlobalFooterEvents.partnerServicesGradConnection
    },
    {
      linkText: t("GO1"),
      href: PARTNER_SERVICES_URL_GO1,
      analytics: analyticsEvents.TRACK_GO1,
      analyticsEvent: analyticsGlobalFooterEvents.partnerServicesGo1
    },
    {
      linkText: t("JobAdder"),
      href: INTERNATIONAL_PARTNER_URL_JOBADDER,
      analytics: analyticsEvents.TRACK_JOBADDER,
      analyticsEvent: analyticsGlobalFooterEvents.partnerServicesJobAdder
    }
  ];
  return {
    partnerLinksRow
  };
};
const PartnerServiceLinks = ({
  onTrackedItemInteraction = () => {
  }
}) => {
  const { partnerLinksRow } = usePartnerServiceLinksItems();
  const handleClick = (event) => {
    if (event) {
      onTrackedItemInteraction(event);
    }
  };
  const renderLink = ({ href, linkText, analytics, analyticsEvent }, index) => /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(
    TextLink,
    {
      href,
      rel: "noopener noreferrer",
      data: { analytics },
      onClick: () => {
        handleClick(analyticsEvent);
      },
      children: linkText
    }
  ) }, index);
  return /* @__PURE__ */ jsx(Box, { id: "footer-partner-services", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsx(
      Box,
      {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        style: { flexWrap: "wrap" },
        children: partnerLinksRow.map(renderLink).reduce(
          // @ts-expect-error
          addSeparatorBetween
        )
      }
    )
  ] }) });
};
const EmployerFooter = ({
  basic = false,
  identityContext: identity,
  localisedContactInfo,
  advertiser,
  linkRenderer = DefaultLinkRenderer,
  locale,
  site,
  isStaging,
  onTrackedItemInteraction = () => {
  }
}) => {
  const { t } = useTranslations(translations$3);
  const [isInternationalPartnersVisible, setIsInternationalPartnersVisible] = useState(false);
  const [isPartnerServicesVisible, setIsPartnerServicesVisible] = useState(false);
  const handleClickInternationalPartners = (event) => {
    event.preventDefault();
    const showInternationalPartners = !isInternationalPartnersVisible;
    setIsInternationalPartnersVisible(showInternationalPartners);
    if (showInternationalPartners) {
      setIsPartnerServicesVisible(false);
    }
  };
  const handleClickPartnerServices = (event) => {
    event.preventDefault();
    const showPartnerServices = !isPartnerServicesVisible;
    setIsPartnerServicesVisible(showPartnerServices);
    if (showPartnerServices) {
      setIsInternationalPartnersVisible(false);
    }
  };
  const internationalPartnersChevronDirection = isInternationalPartnersVisible ? "up" : "down";
  const partnerServicesChevronDirection = isPartnerServicesVisible ? "up" : "down";
  const authenticationStatus = getAuthenticationStatus(identity);
  const isAgency = Boolean(advertiser == null ? void 0 : advertiser.isAgency);
  const permissionOptions = (advertiser == null ? void 0 : advertiser.permissionOptions) ?? defaultPermissionOptions;
  const currentYear = (/* @__PURE__ */ new Date()).getFullYear().toString();
  const urlResolver = createUrlResolver({
    site,
    language: locale,
    staging: isStaging
  });
  const CopyrightIcon = () => /* @__PURE__ */ jsx(Fragment, { children: "© " });
  const handleItemInteraction = (event) => {
    onTrackedItemInteraction({
      ...event,
      accountPermissions: permissionOptions,
      authenticationStatus,
      isAgency,
      isSwitchAccountEnabled: Boolean(advertiser == null ? void 0 : advertiser.switchAccountsHref),
      isCreditOnHold: Boolean(advertiser == null ? void 0 : advertiser.showOnCreditHoldMessage)
    });
  };
  return /* @__PURE__ */ jsxs("footer", { children: [
    basic || isAuthenticated(authenticationStatus) && !isAgency && /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: /* @__PURE__ */ jsx(
      Box,
      {
        paddingX: "gutter",
        paddingY: ["small", "large"],
        className: authLinksContainer,
        children: /* @__PURE__ */ jsx(ContentBlock, { children: /* @__PURE__ */ jsx(
          AuthenticatedFooterLinks,
          {
            linkRenderer,
            permissionOptions,
            site,
            localisedContactInfo,
            urlResolver,
            onTrackedItemInteraction: handleItemInteraction
          }
        ) })
      }
    ) }),
    /* @__PURE__ */ jsx(
      Box,
      {
        paddingX: "gutter",
        paddingY: ["small", "large"],
        className: generalLinksContainer,
        children: /* @__PURE__ */ jsx(ContentBlock, { width: "large", children: /* @__PURE__ */ jsxs(Stack, { space: "small", align: "left", children: [
          /* @__PURE__ */ jsx(
            GeneralLinks,
            {
              internationalPartnersChevronDirection,
              partnerServicesChevronDirection,
              linkRenderer,
              site,
              urlResolver,
              onClickInternationalPartners: handleClickInternationalPartners,
              onClickPartnerServices: handleClickPartnerServices,
              localisedContactInfo,
              onTrackedItemInteraction
            }
          ),
          isPartnerServicesVisible && /* @__PURE__ */ jsx(
            PartnerServiceLinks,
            {
              onTrackedItemInteraction
            }
          ),
          isInternationalPartnersVisible && /* @__PURE__ */ jsx(
            InternationalPartnerLinks,
            {
              onTrackedItemInteraction
            }
          ),
          /* @__PURE__ */ jsx(
            Text,
            {
              tone: "secondary",
              size: "small",
              weight: "regular",
              align: ["left", "center"],
              children: t("Seek All rights reserved", {
                currentYear,
                CopyrightIcon: () => /* @__PURE__ */ jsx(CopyrightIcon, {})
              })
            }
          )
        ] }) })
      }
    )
  ] });
};
export {
  DefaultLinkRenderer,
  EmployerFooter,
  EmployerHeaderProvider,
  defaultPermissionOptions,
  getAuthenticationStatus,
  isAuthenticated,
  isPending,
  useEmployerHeaderContext
};
